import { joiResolver } from '@hookform/resolvers/joi';
import TrashOutline from 'assets/svg/icons/TrashOutline';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import DatePicker from 'components/atoms/DatePicker/DatePicker.component';
import { Checkbox } from 'components/atoms/Form/Checkbox';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import { Select } from 'components/atoms/Form/Select';
import { Loader } from 'components/atoms/Loader';
import RepeatIcon from 'components/atoms/SVG/RepeatIcon.component';
import { Helper } from 'components/molecules/Helper';
import dayjs from 'dayjs';
import useFeatures from 'hooks/useFeatures';
import useSteps from 'hooks/useSteps';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import ReactMarkdown from 'react-markdown';
import { useMutation } from 'react-query';
import { ExpenseRecurrenceFrequency } from 'types/expenses.types';
import { logAmplitude } from 'utils/amplitude';
import * as ExpensesEvents from 'utils/amplitude/events/expenses.amplitude';
import { CalendarIconOutline, EditOutlineIcon } from 'utils/icons';
import { InfoIconOutline } from 'utils/icons/figmaIcons';
import {
  frequencyOptions,
  getFirstRecurrenceDate,
  getStartDateBasedOnExpenseDate,
} from './helpers';
import checkAnimationData from './lottie-check.json';
import schema from './schema';
import {
  ConfirmationProps,
  DeleteRecurringProps,
  DoneProps,
  FieldsProps,
  Flows,
  FormValues,
  RecurringFormProps,
  Screens,
} from './types';

export const RecurringScheduled: FC<DoneProps> = ({ firstRecurrenceDate }) => {
  const { t } = useTranslation();

  useEffect(() => {
    logAmplitude(ExpensesEvents.WEBAPP_RECURRINGEXPENSE_CONFIRMATIONSHOWN);
  }, []);

  return (
    <div>
      <Lottie
        height={42}
        width={42}
        options={{
          loop: false,
          autoplay: true,
          animationData: checkAnimationData,
        }}
      />

      <p className="font-semibold text-xl text-center my-2">
        {t('expense.recurring.done.title')}
      </p>

      <p className="text-center text-primary-500 leading-tight max-w-lg mx-auto mb-6">
        <span className="block">
          {t('expense.recurring.done.desc1', { date: firstRecurrenceDate })}
        </span>
        {t('expense.recurring.done.desc2')}
      </p>
    </div>
  );
};

const Summary: FC<ConfirmationProps> = ({
  onCancel,
  onSubmit,
  onEdit,
  data,
  okText,
  cancelText,
  okButtonProps,
  cancelButtonProps,
  hint,
}) => {
  const { t } = useTranslation();
  const dateFormat = useFeatures('formats.date');

  const { frequency, start_date, end_date, include_file, expense_date } = data;

  useEffect(() => {
    logAmplitude(ExpensesEvents.WEBAPP_RECURRINGEXPENSE_RECURRENCESUMMARY);
  }, []);

  return (
    <div>
      <div className="relative h-10 w-10 bg-expenses-100 rounded-full mx-auto">
        <RepeatIcon className="w-6 h-6 text-expenses abs-center" />
      </div>

      <p className="font-semibold text-xl text-center mt-2 mb-6">
        {t('expense.recurring.summary.title')}
      </p>

      <div className="flex gap-2 py-4 pl-4 border border-primary-100 rounded-lg">
        <div className="relative flex-shrink-0 h-10 w-10 bg-primary-50 rounded-full">
          <CalendarIconOutline className="w-5 h-5 text-primary abs-center" />
        </div>
        <div className="flex-1">
          <div className="text-sm">
            <span className="text-primary-600">
              {t('expense.recurring.form.frequency')}:
            </span>{' '}
            <span>{t(`expense.recurring.form.frequency.${frequency}`)}</span>
          </div>
          {!!expense_date && (
            <div className="text-sm">
              <span className="text-primary-600">
                {t('expense.recurring.form.original_date')}:
              </span>{' '}
              <span>{dayjs(expense_date).format(dateFormat)}</span>
            </div>
          )}
          <div className="text-sm">
            <span className="text-primary-600">
              {t('expense.recurring.form.start_date')}:
            </span>{' '}
            <span>{dayjs(start_date).format(dateFormat)}</span>
          </div>
          {!!end_date && (
            <div className="text-sm">
              <span className="text-primary-600">
                {t('expense.recurring.form.end_date')}:
              </span>{' '}
              <span>{dayjs(end_date).format(dateFormat)}</span>
            </div>
          )}
          <div className="text-sm">
            <span className="text-primary-600">
              {t('expense.recurring.form.copy_file')}:
            </span>{' '}
            <span>{include_file ? t('yes') : t('no')}</span>
          </div>
        </div>
        {!!onEdit && (
          <Button
            asDefault
            onClick={onEdit}
            className="p-4 flex-shrink-0 self-center text-primary-600 hover:text-expenses"
          >
            <EditOutlineIcon className="w-6 h-6" />
          </Button>
        )}
      </div>

      {!!hint && (
        <div className="mt-2.5 flex gap-1 text-primary-600">
          <InfoIconOutline className="w-5 h-5 flex-shrink-0 relative -top-[0.5px]" />
          {hint}
        </div>
      )}

      <div className="flex gap-3 mt-8">
        <Button
          structure="secondary"
          className="flex-1"
          onClick={onCancel}
          {...cancelButtonProps}
        >
          {cancelText || t('cancel')}
        </Button>
        <Button className="flex-1" onClick={onSubmit} {...okButtonProps}>
          {okText || t('confirm')}
        </Button>
      </div>
    </div>
  );
};

const DeleteConfirmation: FC<{
  onCancel: () => void;
  onSubmit: () => Promise<void> | void;
}> = ({ onCancel, onSubmit }) => {
  const { t } = useTranslation();

  const submitMutation = useMutation({
    mutationFn: () => Promise.resolve().then(onSubmit),
  });

  return (
    <div>
      <div className="relative h-10 w-10 bg-expenses-100 rounded-full mx-auto">
        <RepeatIcon className="w-6 h-6 text-expenses abs-center" />
      </div>

      <p className="font-semibold text-xl text-center mt-2">
        {t('expense.recurring.delete.title')}
      </p>

      <p className="text-center text-primary-500 leading-tight max-w-lg mx-auto mt-2">
        {t('expense.recurring.delete.desc')}
      </p>

      <div className="flex gap-3 mt-8">
        <Button
          structure="secondary"
          color="light-red"
          className="flex-1"
          onClick={onCancel}
          disabled={submitMutation.isLoading}
          tracingEvent={
            ExpensesEvents.WEBAPP_RECURRINGEXPENSE_CANCELSTOPRECURRENCE
          }
        >
          {t('no')}
        </Button>
        <Button
          className="flex-1"
          color="red"
          onClick={() => submitMutation.mutate()}
          disabled={submitMutation.isLoading}
          tracingEvent={
            ExpensesEvents.WEBAPP_RECURRINGEXPENSE_CONFIRMSTOPRECURRENCE
          }
        >
          {submitMutation.isLoading && <Loader size="sm" className={'mr-1'} />}
          {t('yes')}
        </Button>
      </div>
    </div>
  );
};

const Fields: FC<FieldsProps> = ({
  onCancel,
  onSubmit,
  firstRecurrenceDate,
  originalExpenseIncludesFile,
}) => {
  const { t } = useTranslation();

  const formAPI = useFormContext<Partial<FormValues>>();

  const { include_file, expense_date, end_date, start_date } = formAPI.watch();

  const minStartDate = useMemo(() => {
    return dayjs
      .max([dayjs(expense_date).startOf('day'), dayjs().startOf('day')])
      .format('YYYY-MM-DD');
  }, [expense_date]);

  useEffect(() => {
    logAmplitude(ExpensesEvents.WEBAPP_RECURRINGEXPENSE_SCHEDULERECURRENCE);
  }, []);

  return (
    <div>
      <div className="relative h-10 w-10 bg-expenses-100 rounded-full mx-auto">
        <RepeatIcon className="w-6 h-6 text-expenses abs-center" />
      </div>

      <p className="font-semibold text-xl text-center my-2">
        {t('expense.recurring.create.title')}
      </p>
      <p className="text-center text-primary-500 leading-tight max-w-lg mx-auto mb-6">
        {t('expense.recurring.create.desc')}
      </p>

      <div className="pt-4 flex flex-col gap-6">
        <FieldConnector
          name="frequency"
          label={t('expense.recurring.form.frequency')}
          className="flex-1 min-w-0 relative z-10"
          helper={
            <Helper>
              <Helper.Title>
                {t('expense.recurring.form.frequency.helper.title')}
              </Helper.Title>
              <Helper.Paragraph>
                <ReactMarkdown>
                  {t('expense.recurring.form.frequency.helper.desc')}
                </ReactMarkdown>
              </Helper.Paragraph>
            </Helper>
          }
        >
          {({ field }) => (
            <Select
              options={frequencyOptions}
              formatOptionLabel={(option) =>
                t(`expense.recurring.form.frequency.${option.value}`)
              }
              isSearchable={false}
              tracing={{
                onChange: {
                  name: ExpensesEvents.WEBAPP_RECURRINGEXPENSE_SCHEDULEFREQUENCY,
                  data: (option: any) => ({ frequency: option?.value }),
                },
              }}
              {...field}
              onChange={(option) => {
                if (option.value === field.value) return;
                field.onChange(option.value);
                formAPI.trigger();
                if (end_date) return;
                const guessCurrentStartDate = getStartDateBasedOnExpenseDate(
                  field.value,
                  expense_date,
                );
                // if the guess not equal to the actual, it means user changed it
                // since the field is not dirty it means the form was saved and re-opened
                if (guessCurrentStartDate !== start_date) return;

                // since the guess is the same as the actual, it means the user was okay with it
                // so we will guess a new one based on the new freq
                const newStartDate = getStartDateBasedOnExpenseDate(
                  option.value,
                  expense_date,
                );
                if (!newStartDate) return;
                formAPI.setValue('start_date', newStartDate);
              }}
            />
          )}
        </FieldConnector>

        <div className="flex gap-3">
          <FieldConnector
            name="start_date"
            label={t('expense.recurring.form.start_date')}
            className="flex-1 min-w-0"
            helper={
              <Helper>
                <Helper.Title>
                  {t('expense.recurring.form.start_date.helper.title')}
                </Helper.Title>
                <Helper.Paragraph>
                  {t('expense.recurring.form.start_date.helper.desc')}
                </Helper.Paragraph>
              </Helper>
            }
          >
            {({ field: { value, onChange, ...field } }) => (
              <DatePicker
                {...field}
                selected={value}
                minDate={minStartDate}
                popperPlacement="top"
                tracing={{
                  onChange: ExpensesEvents.WEBAPP_RECURRINGEXPENSE_FROMDATE,
                }}
                onChange={(date) => {
                  const value = date ? dayjs(date).format('YYYY-MM-DD') : null;
                  if (!value) return;
                  onChange(value);
                  formAPI.trigger();
                }}
              />
            )}
          </FieldConnector>
          <FieldConnector
            name="end_date"
            label={t('expense.recurring.form.end_date')}
            className="flex-1 min-w-0"
            optional
          >
            {({ field: { value, onChange, ...field } }) => (
              <DatePicker
                {...field}
                selected={value}
                minDate={minStartDate}
                placeholder={t('invoice.field.recurring.end_date.placeholder')}
                popperPlacement="top"
                tracing={{
                  onChange: ExpensesEvents.WEBAPP_RECURRINGEXPENSE_TODATE,
                }}
                onChange={(date) => {
                  const value = date ? dayjs(date).format('YYYY-MM-DD') : null;
                  onChange(value);
                  formAPI.trigger();
                }}
              />
            )}
          </FieldConnector>
        </div>
        <FieldConnector name="include_file">
          {({ field }) => (
            <>
              <div className="flex">
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={!!field.value}
                    onChange={field.onChange}
                    tracingEvent={
                      ExpensesEvents.WEBAPP_RECURRINGEXPENSE_COPYATTACHMENT
                    }
                  />
                  <span className="flex items-center gap-2">
                    {t('expense.recurring.form.copy_file')}{' '}
                    <Helper>
                      <Helper.Paragraph>
                        {t('expense.recurring.form.copy_file.helper')}
                      </Helper.Paragraph>
                    </Helper>
                  </span>
                </label>
              </div>
              {!originalExpenseIncludesFile && include_file && (
                <Alert
                  type="info"
                  className="mb-0 mt-2"
                  title={t('expense.recurring.form.copy_file.info')}
                />
              )}
            </>
          )}
        </FieldConnector>
      </div>
      {firstRecurrenceDate && (
        <Alert
          type="info"
          className="mb-0 mt-6"
          title={t('expense.recurring.create.info', {
            date: firstRecurrenceDate,
          })}
        />
      )}
      <div className="flex gap-3 mt-8">
        <Button
          structure="secondary"
          color="light-expenses"
          className="flex-1"
          onClick={onCancel}
          tracingEvent={ExpensesEvents.WEBAPP_RECURRINGEXPENSE_SCHEDULECANCEL}
        >
          {t('cancel')}
        </Button>
        <Button className="flex-1" onClick={() => onSubmit()}>
          {t('continue')}
        </Button>
      </div>
    </div>
  );
};

const flows: Record<Flows, Screens[]> = {
  'create/edit': ['create/edit-form', 'create/edit-summary'],
  'delete/edit': ['delete/edit-summary'],
};

export const RecurringForm: FC<RecurringFormProps> = ({
  flow: initialFlow,
  defaultValues,
  originalExpenseIncludesFile,
  onCancel,
  onSubmit,
  onDone,
}) => {
  const { t } = useTranslation();

  const dateFormat = useFeatures('formats.date');

  const defaultFrequency =
    defaultValues.frequency || ExpenseRecurrenceFrequency.monthly;

  const defaultExpenseDate = defaultValues.expense_date;

  const defaultStartDate = defaultValues.start_date;

  const initialStartDate = useMemo(() => {
    return (
      defaultStartDate ||
      getStartDateBasedOnExpenseDate(defaultFrequency, defaultExpenseDate) ||
      undefined
    );
  }, [defaultStartDate, defaultFrequency, defaultExpenseDate]);

  const formAPI = useForm({
    resolver: joiResolver(schema, {
      allowUnknown: true,
      abortEarly: false,
    }),
    defaultValues: {
      frequency: defaultFrequency,
      start_date: initialStartDate,
      end_date: defaultValues.end_date || undefined,
      expense_date: defaultExpenseDate,
      include_file: !!defaultValues.include_file,
    },
  });

  console.log(formAPI.formState.errors);

  const formValues = formAPI.watch();

  const { frequency, start_date, end_date, expense_date, include_file } =
    formValues;

  const firstRecurrenceDate = getFirstRecurrenceDate(
    frequency,
    start_date,
    expense_date,
  );

  const summaryHint = (
    <div className="text-xs">
      <p>
        {t('expense.recurring.summary.hint1', {
          date: dayjs(firstRecurrenceDate).format(dateFormat),
        })}
      </p>
      <p>{t('expense.recurring.summary.hint2')}</p>
    </div>
  );

  const components = {
    'create/edit-form': (
      <FormProvider key="fields" {...formAPI}>
        <Fields
          firstRecurrenceDate={dayjs(firstRecurrenceDate).format(dateFormat)}
          originalExpenseIncludesFile={originalExpenseIncludesFile}
          onCancel={onCancel}
          onSubmit={() => {
            formAPI.trigger().then((isValid) => {
              if (isValid) stepsAPI.next();
            });
          }}
        />
      </FormProvider>
    ),
    'create/edit-summary': (
      <Summary
        key="create/edit-summary"
        data={{
          frequency,
          start_date,
          end_date,
          include_file,
          expense_date,
        }}
        hint={summaryHint}
        cancelText={t('edit')}
        onCancel={() => stepsAPI.back()}
        cancelButtonProps={{
          color: 'light-expenses',
        }}
        okButtonProps={{
          tracingEvent: ExpensesEvents.WEBAPP_RECURRINGEXPENSE_SCHEDULECONFIRM,
        }}
        onSubmit={formAPI.handleSubmit((data) => {
          onSubmit({
            ...data,
            next_date: firstRecurrenceDate,
          } as FormValues);
          onDone();
        })}
      />
    ),
    'delete/edit-summary': (
      <Summary
        key="delete/edit-summary"
        data={{
          frequency,
          start_date,
          end_date,
          include_file,
          expense_date,
        }}
        hint={summaryHint}
        okText={
          <span className="flex items-center gap-1">
            <TrashOutline className="w-5 h-5" /> {t('delete')}
          </span>
        }
        okButtonProps={{
          color: 'red',
          tracingEvent:
            ExpensesEvents.WEBAPP_RECURRINGEXPENSE_CLICKDELETERECURRENCE,
        }}
        cancelButtonProps={{
          color: 'light-expenses',
        }}
        cancelText={t('edit')}
        onCancel={() => {
          stepsAPI.jump(0);
          changeFlow('create/edit');
        }}
        onSubmit={() => {
          onSubmit(null);
          onDone();
        }}
      />
    ),
  };

  const [flow, changeFlow] = useState(initialFlow);

  const screens = flows[flow].map((screen) => components[screen]);

  const stepsAPI = useSteps(screens.length - 1);

  return screens[stepsAPI.step];
};

export const DeleteRecurring: FC<DeleteRecurringProps> = ({
  data,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const dateFormat = useFeatures('formats.date');

  const next_date = dayjs(data.next_date).format(dateFormat);

  const screens = [
    <Summary
      key="summary"
      data={{
        frequency: data.frequency,
        start_date: data.start_date,
        end_date: data.end_date,
        include_file: !!data.template?.file,
        expense_date: data.original_expense_date,
      }}
      hint={
        <div className="text-xs">
          <p>
            {t('expense.recurring.summary.hint1', {
              date: next_date,
            })}
          </p>
          <p>{t('expense.recurring.summary.hint2.stop')}</p>
        </div>
      }
      onCancel={onCancel}
      onSubmit={() => stepsAPI.next()}
      okText={
        <span className="flex items-center gap-1">
          <TrashOutline className="w-5 h-5" />{' '}
          {t('expense.recurring.stop_recurrence')}
        </span>
      }
      cancelText={t('close')}
      okButtonProps={{
        color: 'red',
        structure: 'secondary',
        tracingEvent:
          ExpensesEvents.WEBAPP_RECURRINGEXPENSE_CLICKSTOPRECURRENCE,
      }}
      cancelButtonProps={{
        color: 'light-red',
      }}
    />,
    <DeleteConfirmation
      key="delete-confirm"
      onCancel={onCancel}
      onSubmit={onSubmit}
    />,
  ];

  const stepsAPI = useSteps(screens.length - 1);

  return screens[stepsAPI.step];
};
